import React, { useCallback } from "react";
import { FlatList } from "react-native";
import { graphql, useLazyLoadQuery } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../../components/mobile/QueryNetworkBoundrary";
import { useIntlTokens } from "../../../../../../components/mobile/useIntlTokens";
import { WACategoryFooterAdminButton } from "../../WACategoryAdminButtons";
import { AHSeparator, AHText, AHTodoCategoryIcon, ListItem } from "arkh";
import { FormattedMessage } from "react-intl";
import { WACategoryTodoCategoriesScreenProps } from "./WACategoryTodoCategoriesNavigator";
import { WACategoryTodoCategoriesSelectCategoryQuery } from "./__generated__/WACategoryTodoCategoriesSelectCategoryQuery.graphql";
import { useWACategoryLayout } from "../../utils/useWACategoryLayout";

type Props =
  WACategoryTodoCategoriesScreenProps<"WACategoryTodoCategoriesSelectCategory">;

export function WACategoryTodoCategoriesSelectCategory(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <InnerWACategoryTodoCategories {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWACategoryTodoCategories(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<WACategoryTodoCategoriesSelectCategoryQuery>(
    graphql`
      query WACategoryTodoCategoriesSelectCategoryQuery {
        store {
          todoCategories(includeIntegrations: false) {
            id
            icon
            title
          }
        }
      }
    `,
    {},
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  type ItemType = (typeof data.store.todoCategories)[number];
  const renderItem = useCallback(
    ({ item, index }: { item: ItemType; index: number }) => {
      return (
        <ListItem
          left={item.icon ? <AHTodoCategoryIcon icon={item.icon} /> : null}
          isFirst={index === 0}
          isLast={index === data.store.todoCategories.length - 1}
          right={<ListItem.NavArrow />}
          onPress={() => {
            props.navigation.navigateDeprecated(
              "WACategoryTodoCategoriesSelectType",
              {
                defaultTeams: props.route.params.defaultTeams,
                position: props.route.params.position,
                storecastId: props.route.params.storecastId,
                unitSize: props.route.params.unitSize,
                maxSize: props.route.params.maxSize,
                categoryId: item.id,
              }
            );
          }}
        >
          <ListItem.Title>{item.title}</ListItem.Title>
        </ListItem>
      );
    },
    [data.store.todoCategories.length, props.navigation, props.route.params]
  );

  const intl_tokens = useIntlTokens();
  const navigateDeprecatedToAdmin = useCallback(() => {
    props.navigation.navigateDeprecated("StoreAdmin", {
      screen: "TodoCategoryAdminList",
      params: { standalone: true },
    });
  }, [props.navigation]);

  const contentContainerStyle = useWACategoryLayout();

  return (
    <FlatList
      style={{ flex: 1 }}
      contentContainerStyle={contentContainerStyle}
      data={data.store.todoCategories}
      renderItem={renderItem}
      ItemSeparatorComponent={AHSeparator}
      ListHeaderComponent={
        <AHText
          type="title-small"
          spaceBottom="medium"
          spaceHorizontal="medium"
        >
          <FormattedMessage
            defaultMessage="Choose Category"
            description="WACategoryTodoCategoriesSelectCategory title"
          />
        </AHText>
      }
      ListFooterComponent={
        <>
          <AHSeparator spaceVertical="large" spaceHorizontal="medium" />
          <WACategoryFooterAdminButton
            title={intl_tokens.CustomListAdmin}
            onPress={navigateDeprecatedToAdmin}
          />
        </>
      }
    />
  );
}
